// Layer 3 (Transformation): NSR & prices — the three-across NSR row, the metal
// price table and the recovery-lever panel.
//
// Per-metal Budget does not exist: `nsrRead().budgetDaily` is a scalar total,
// and the engine's per-product breakdown is module-private. So only the total
// card carries a comparator, and it names its basis — the design-recovery
// figure, labelled **design potential** so it is never a second Budget beside
// the GM landing's M5 Budget. The per-metal figure the engine *can* give per
// product is the recovery shortfall, summed over this route's own seeded days —
// but that is not the whole design gap either (at Month to date the gap is
// $89,877 while the recovery shortfall sums to $50,453; the residual is the Zn
// concentrate penalty and a throughput basis difference), so it is never
// presented as a design-potential delta.
//
// The lever panel's three figures multiply out: a mean gap in recovery points,
// the period cost of that gap, and the derived cost per point per day. The
// lever is never `leverPerPt × price` — that is gross payable metal value, not
// NSR.

import { formatMoney, recoveryPayableLevers, recoveryShortfallCost } from '@/versions/v1/shared/NsrEngine';
import { BUNKER_HILL_SMELTER_SCHEDULE } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { INITIAL_STATE } from '@/versions/v1/shared/ScenarioEngine';
import { formatCurrency, formatCurrencyExact, CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import {
  budgetRevenueForDay,
  costPeriod,
  daysForPeriod,
  stateForDay,
  type CostPeriod,
  type CostPeriodView,
  type RevenueRow,
} from './Scene.transform';
import { COST_SEED_PROVENANCE } from './seed';
import type { CostKpiCard } from './Scene.transform';

/** Each card arrives with its money twice: the `CostKpiCard` the row paints,
 *  and the raw terms that card was formatted from. Without them the only thing
 *  a probe can check on this row is a substring of the card's sub — the three
 *  card *values* would be untested, so `formatMoney(pb)` in place of
 *  `formatMoney(pb + ag)` would ship green. */
export type NsrCard = CostKpiCard & { raw: number };

export type NsrCards = {
  pbAg: NsrCard;
  zn: NsrCard;
  total: NsrCard;
  /** The card values unformatted. `pbAgRaw` is the sum of the two product nets
   *  the card prints, so `pbAgRaw === pbTotal + agTotal` is a real assertion
   *  rather than a restatement of the card's own field. */
  totalRaw: number;
  pbAgRaw: number;
  znRaw: number;
  /** The total card's Budget comparator — the same sum the route's Budget uses. */
  budgetRaw: number;
};

/** The comparator basis both the total card and the route's Budget use, so the
 *  card and the P&L cannot quote different Budget revenue for one period. */
export function nsrBudgetRevenue(period: CostPeriod): number {
  return daysForPeriod(period).reduce((sum, day) => sum + budgetRevenueForDay(day), 0);
}

export function nsrCards(period: CostPeriod): NsrCards {
  const view: CostPeriodView = costPeriod(period);
  const budgetTotal = nsrBudgetRevenue(period);
  const { pb, zn, ag, total } = view.revenueByProduct;
  const gap = budgetTotal - total;

  return {
    pbAg: {
      label: 'Pb / Ag NSR',
      value: formatMoney(pb + ag),
      // The per-metal cards carry no Budget comparator — the engine reads one
      // scalar budget total, not a per-product one. They name their basis
      // instead of asserting a delta they cannot prove.
      sub: 'Payable × price − TC/RC − freight',
      tone: 'info',
      raw: pb + ag,
    },
    zn: {
      label: 'Zn NSR',
      value: formatMoney(zn),
      sub: 'Payable × price − TC/RC − freight − penalty',
      tone: 'warn',
      raw: zn,
    },
    total: {
      label: 'Total NSR',
      value: formatMoney(total),
      // Cost's revenue comparator is the design-recovery figure, named design
      // potential (never a second Budget — the GM landing's Budget is the M5
      // target). A design-potential gap is not a budget miss, so the tone is
      // neutral in both directions, matching the landing's caption.
      sub:
        gap >= 0
          ? `design potential ${formatMoney(budgetTotal)} · ${formatMoney(gap)} under`
          : `design potential ${formatMoney(budgetTotal)} · ${formatMoney(Math.abs(gap))} over`,
      tone: 'neutral',
      raw: total,
    },
    totalRaw: total,
    pbAgRaw: pb + ag,
    znRaw: zn,
    budgetRaw: budgetTotal,
  };
}

export type MetalPriceRow = {
  metal: string;
  unit: string;
  /** The one K9 price deck (`BUNKER_HILL_SMELTER_SCHEDULE.prices`) — the same
   *  deck `NsrEngine` prices both the actual and the Budget/design potential
   *  columns with, so the bridge's price step is zero by construction. */
  price: number;
  priorWeek: number;
  volume: string;
  revenue: string;
};

export type MetalPriceView = {
  label: string;
  dateRange: string;
  rows: readonly MetalPriceRow[];
  provenance: string;
};

/** One price per metal: the K9 deck, the same US$ deck the money function
 *  (`NsrEngine` under D21) prices budget and actual with. The prior-week
 *  column is a comparator on that same schedule; there is no separate
 *  budget-price or spread column, because a second deck here is exactly the
 *  spread the GM landing's zero price step says does not exist. */
export function metalPriceTable(period: CostPeriod): MetalPriceView {
  const view = costPeriod(period);
  const prices = BUNKER_HILL_SMELTER_SCHEDULE.prices;
  const rowFor = (metal: 'Pb' | 'Zn' | 'Ag') =>
    metal === 'Pb'
      ? view.revenueRows.find((r) => r.product === 'Pb concentrate')!
      : metal === 'Zn'
        ? view.revenueRows.find((r) => r.product === 'Zn concentrate')!
        : view.revenueRows.find((r) => r.product === 'Ag payable')!;

  const rows: MetalPriceRow[] = (['Pb', 'Zn', 'Ag'] as const).map((metal) => {
    const row = rowFor(metal);
    const price = metal === 'Pb' ? prices.pbPerTonne : metal === 'Zn' ? prices.znPerTonne : prices.agPerOz;
    const priorWeek = metal === 'Pb' ? prices.priorWeek.pbPerTonne : metal === 'Zn' ? prices.priorWeek.znPerTonne : prices.priorWeek.agPerOz;
    return {
      metal,
      unit: metal === 'Ag' ? `${CURRENCY_GLYPH}/oz` : `${CURRENCY_GLYPH}/dmt`,
      price,
      priorWeek,
      volume: row.payableMetal,
      revenue: row.netRevenue,
    };
  });

  return { label: view.label, dateRange: view.dateRange, rows, provenance: BUNKER_HILL_SMELTER_SCHEDULE.prices.comparisonProvenance };
}

export type RecoveryLeverRow = {
  metal: string;
  meanGapPt: number;
  gapPt: string;
  periodCostRaw: number;
  periodCost: string;
  costPerPtPerDay: string;
};

/** Period cost stays fully legible; the per-point rate reads the same current
 * engine basis as Capacity tab 2. */
export type RecoveryLeverView = {
  dayCount: number;
  rows: readonly RecoveryLeverRow[];
  provenance: string;
};

export function recoveryLeverPanel(period: CostPeriod): RecoveryLeverView {
  const days = daysForPeriod(period);
  const dayCount = days.length;
  const currentLevers = recoveryPayableLevers(INITIAL_STATE);

  const rows: RecoveryLeverRow[] = (['Pb', 'Zn', 'Ag'] as const).map((metal) => {
    const gaps: number[] = [];
    let periodCost = 0;
    for (const day of days) {
      const state = stateForDay(day.date);
      const lever = recoveryPayableLevers(state).find((l) => l.metal === metal);
      if (lever) gaps.push(lever.gapPt);
      const shortfall = recoveryShortfallCost(state).metals.find((m) => m.metal === metal);
      if (shortfall) periodCost += shortfall.dailyCost;
    }
    const meanGapPt = gaps.length > 0 ? gaps.reduce((sum, g) => sum + g, 0) / gaps.length : 0;
    const printedGapPt = Math.round(Math.abs(meanGapPt) * 100) / 100;
    return {
      metal,
      meanGapPt,
      gapPt: `${meanGapPt >= 0 ? '+' : '−'}${printedGapPt.toFixed(2)} pt`,
      periodCostRaw: periodCost,
      periodCost: formatCurrencyExact(periodCost),
      costPerPtPerDay: formatCurrencyExact(currentLevers.find((row) => row.metal === metal)!.moneyPerPtA),
    };
  });

  return { dayCount, rows, provenance: COST_SEED_PROVENANCE };
}

/** One decimal policy per column, not per value: a US$/t price is quoted in
 *  whole dollars, a US$/oz price in cents. */
function formatPrice(n: number, unit: string): string {
  return unit === `${CURRENCY_GLYPH}/oz` ? formatCurrencyExact(n) : formatCurrency(n);
}

export type RevenueByConcentrateRow = RevenueRow & {
  /** `vs US$2,240 last week`: the same schedule's prior-week price, set under
   *  the price it compares (polish.P7 folds the metal-price table into this
   *  column; the payable and revenue it also printed are this row's). */
  priorWeek: string;
};

export function revenueByConcentrate(period: CostPeriod): readonly RevenueByConcentrateRow[] {
  const prices = metalPriceTable(period).rows;
  const metalOf: Record<RevenueRow['product'], string> = {
    'Pb concentrate': 'Pb',
    'Zn concentrate': 'Zn',
    'Ag payable': 'Ag',
  };
  return costPeriod(period).revenueRows.map((row) => {
    const price = prices.find((p) => p.metal === metalOf[row.product])!;
    return { ...row, priorWeek: `vs ${formatPrice(price.priorWeek, price.unit)} last week` };
  });
}

/** The lever panel's rate column heading, from the transform layer like every
 *  money string on this route. */
export const PER_PPT_DAY_HEADING = `${CURRENCY_GLYPH}/pt/day`;
